import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { carName } from '../../domain/cars/carCatalogue';
import { formatCompactPrice, formatPrice } from '../../domain/cars/carMoney';
import { decadeTicks, investmentRating, valueHistory } from '../../domain/cars/carValue';
import {
    compareFrame,
    logProjector,
    polyline,
    SERIES_CLASS,
} from './comparePlot';
import useWideStage from '../../application/motion/useWideStage';

/**
 * What both have been worth, on one pair of axes (SPEC-008 PR-8.5, D-20).
 *
 * THE MONEY AXIS IS LOGARITHMIC and that is the decision this component is
 * arranged around. A $3.05M F40 and a $37K Esprit on one linear axis draw the
 * Esprit as a flat line along the floor — a chart that has quietly hidden one of
 * its two subjects, which is a worse failure than any scale can be. On a log
 * axis equal vertical distances are equal RATIOS, which is the genuinely
 * comparable thing about two assets held for thirty years, and the gridlines
 * still carry real money so nothing is concealed.
 *
 * The single-car band on the detail card is linear and stays linear, which is
 * not an inconsistency: one car has no spread to defeat it, and money has a true
 * zero that a single series should be drawn against. The caption here says the
 * scale is a log one, because a visual convention that is never stated is a
 * convention only its author knows.
 *
 * The sourced points stay solid marks and the estimated years stay hollow ones
 * on a broken line, exactly as `carValue.js` argues. A rating and a multiple sit
 * beside each car in words, because those are the two figures a reader would
 * repeat and neither is readable off a curve.
 *
 * The copy it BORROWS — the money axis, the rating words, the table's columns —
 * is SPEC-002's and SPEC-004's, shared because they name the same facts and two
 * spellings of "Value ($)" is how one band ends up saying something the other
 * does not. The year axis is this screen's own key rather than the detail card's,
 * because that band stopped drawing a year label and its key went with it. The
 * browser suite asserts that nothing on this screen renders a raw message key,
 * which is what makes borrowing safe rather than merely convenient.
 */

/** The gridlines: powers of ten, and the halves between them where there is room. */
function moneyTicks(floor, ceiling) {
    const ticks = [];

    for (let power = Math.ceil(Math.log10(floor)); 10 ** power <= ceiling; power += 1) {
        ticks.push(10 ** power);
    }

    // A single decade of range would draw one gridline, which is a chart a
    // reader cannot measure anything against.
    return ticks.length > 2 ? ticks : [
        ...ticks,
        ...ticks.map((tick) => tick / 2).filter((tick) => tick >= floor),
    ].sort((one, other) => one - other);
}

/**
 * The extent both series are drawn against.
 *
 * The floor is a HALF-DECADE below the cheapest figure rather than at it, so the
 * lowest point is seated on the plot rather than drawn on the frame — the same
 * argument the linear charts make for their headroom, in the units a log axis
 * measures in.
 */
function sharedExtent(histories) {
    const drawn = histories.filter(Boolean);
    const amounts = drawn.flatMap((history) => history.points.map((point) => point.amount));

    return {
        from: Math.min(...drawn.map((history) => history.from)),
        to: Math.max(...drawn.map((history) => history.to)),
        floor: Math.min(...amounts) / 1.8,
        ceiling: Math.max(...amounts) * 1.4,
        currency: drawn[0].currency,
    };
}

function Rating({ car, index }) {
    const t = useTranslate();
    const rating = investmentRating(car);

    if (!rating) {
        return null;
    }

    return (
        <li className={`compare-chart__key ${SERIES_CLASS[index]}`}>
            <span className="compare-chart__swatch" aria-hidden="true" />
            <span className="compare-chart__key-name">{carName(car)}</span>
            <span className="compare-chart__key-note">
                {t('compare.value.rating', {
                    rating: t(`cars.value.rating.${rating.rating}`),
                    multiple: rating.multiple.toFixed(1),
                })}
            </span>
        </li>
    );
}

export default function CompareValueChart({ cars }) {
    const t = useTranslate();
    const { languageCode } = useLocale();
    // Read before the early return below, because a hook has to be: the frame is
    // the drawing box `compare.css` sized the plate for (`useWideStage`).
    const frame = compareFrame(useWideStage());
    const histories = cars.map((car) => valueHistory(car));
    const missing = cars.filter((car, index) => !histories[index]);

    if (histories.every((history) => !history)) {
        return (
            <p className="compare-chart__absent">
                {t('compare.value.neither', { cars: cars.map(carName).join(t('compare.and')) })}
            </p>
        );
    }

    const extent = sharedExtent(histories);
    const project = logProjector(frame, {
        width: extent.to - extent.from,
        floor: extent.floor,
        ceiling: extent.ceiling,
    });
    // The year axis is linear and starts at the earliest launch, so the
    // projector's `x` is fed an offset rather than a year.
    const atYear = (year) => project.x(year - extent.from);

    return (
        <figure className="compare-chart">
            <p className="compare-chart__axis-label" aria-hidden="true">
                {t('cars.value.valueAxis', { currency: extent.currency })}
            </p>

            <svg
                className="compare-chart__plot"
                viewBox={frame.viewBox}
                aria-hidden="true"
                focusable="false"
            >
                <g className="compare-chart__grid">
                    {moneyTicks(extent.floor, extent.ceiling).map((tick) => (
                        <line key={`money-${tick}`} x1={frame.plot.left} x2={frame.right} y1={project.y(tick)} y2={project.y(tick)} />
                    ))}
                    {decadeTicks(extent.from, extent.to).map((tick) => (
                        <line key={`year-${tick}`} x1={atYear(tick)} x2={atYear(tick)} y1={frame.plot.top} y2={frame.floor} />
                    ))}
                </g>

                <path className="compare-chart__axis" d={frame.axisPath} />

                <g className="compare-chart__ticks">
                    {moneyTicks(extent.floor, extent.ceiling).map((tick) => (
                        <text key={`money-label-${tick}`} x={frame.plot.left - 4} y={project.y(tick) + 3} textAnchor="end">
                            {formatCompactPrice({ amount: tick, currency: extent.currency }, { languageCode })}
                        </text>
                    ))}
                    {decadeTicks(extent.from, extent.to).map((tick) => (
                        <text key={`year-label-${tick}`} x={atYear(tick)} y={frame.view.height - 4} textAnchor="middle">
                            {tick}
                        </text>
                    ))}
                </g>

                {histories.map((history, index) => (history ? (
                    <g className={`compare-chart__series ${SERIES_CLASS[index]}`} key={cars[index].id + String(index)}>
                        {/* Broken, because most of this line is a reconstruction
                            — nobody publishes what a 1993 Testarossa was worth
                            in 1998, and the two ends that are documents are the
                            only two points entitled to a solid mark. */}
                        <path
                            className="compare-chart__curve compare-chart__curve--estimated"
                            d={polyline(
                                history.points.map((point) => ({ x: point.year - extent.from, y: point.amount })),
                                project,
                            )}
                        />
                        {history.points.map((point) => (
                            <circle
                                className={`compare-chart__point${point.sourced ? '' : ' compare-chart__point--estimated'}`}
                                key={point.year}
                                cx={atYear(point.year)}
                                cy={project.y(point.amount)}
                                r={point.sourced ? 3.5 : 2.5}
                            />
                        ))}
                    </g>
                ) : null))}
            </svg>

            <p className="compare-chart__axis-label compare-chart__axis-label--end" aria-hidden="true">
                {t('compare.value.yearAxis')}
            </p>

            <ul className="compare-chart__legend">
                {cars.map((car, index) => <Rating car={car} index={index} key={car.id + String(index)} />)}
            </ul>

            <table className="visually-hidden">
                <caption>{t('compare.value.tableCaption')}</caption>
                <thead>
                    <tr>
                        <th scope="col">{t('compare.table.figure')}</th>
                        <th scope="col">{t('cars.value.yearColumn')}</th>
                        <th scope="col">{t('cars.value.valueColumn')}</th>
                        <th scope="col">{t('cars.value.basisColumn')}</th>
                    </tr>
                </thead>
                <tbody>
                    {histories.flatMap((history, index) => (history?.points ?? []).map((point) => (
                        <tr key={`${cars[index].id}-${point.year}`}>
                            <td>{carName(cars[index])}</td>
                            <td>{point.year}</td>
                            <td>{formatPrice({ amount: point.amount, currency: history.currency }, { languageCode })}</td>
                            <td>{t(point.sourced ? 'cars.value.basis.sourced' : 'cars.value.basis.estimated')}</td>
                        </tr>
                    )))}
                </tbody>
            </table>

            <figcaption className="compare-chart__source">
                {missing.length > 0
                    ? t('compare.value.missing', { car: missing.map(carName).join(t('compare.and')) })
                    : null}
                {' '}
                {t('compare.value.logScale')}
            </figcaption>
        </figure>
    );
}
